import React, { useMemo, useState, useEffect } from 'react';
import ReactTable from '../../../components/shared/ReactTable';
import { getDateFormat } from '../../../utils/common-methods';
import FIELD_TYPE from '../../../constants/fieldType';
import { COLUMNS } from '../../../constants/tableColumns';
import { Button } from '../../../components/shared';
import {
  useRemoveModemFromMachineMutation,
  useLazyGetModemResyncStatusQuery,
} from '../../../services/Modems/ModemsService';
import { useLazyGetAssetResyncStatusQuery } from '../../../services/Machines/MachinesService';
import { useSelector } from 'react-redux';
import { Box } from '@mui/material';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import ConfirmationModal from '../../../components/ConfirmationModel';

const { NOT_APPLICABLE } = FIELD_TYPE;

const ModemDetailView = ({
  modemInfo = [],
  refetchModems,
  refetachMachine,
  refetchCurrentMachineDetails,
  machineId,
}) => {
  const [removeModemFromMachine, { isLoading: isRemoving }] = useRemoveModemFromMachineMutation();
  const [getAssetResyncStatus] = useLazyGetAssetResyncStatusQuery();
  const [getModemResyncStatus] = useLazyGetModemResyncStatusQuery();
  const updatedBy = useSelector((state) => state.user?.userDetails?.id);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarError, setSnackbarError] = useState('');
  const [localData, setLocalData] = useState([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [modemToRemove, setModemToRemove] = useState(null);
  const [resyncWarning, setResyncWarning] = useState('');
  const isResyncEnabled = process.env.REACT_APP_ENABLE_FEED_RESYNC === 'true';
  // Sync localData with modemInfo prop
  useEffect(() => {
    if (Array.isArray(modemInfo)) {
      setLocalData(
        modemInfo.map((item) => {
          const modem = item.modem || item;
          const { inEffectFrom, inEffectTo } = item || {};

          return {
            ...modem,
            type: modem?.type || NOT_APPLICABLE,
            serialNumber: modem?.serialNumber || NOT_APPLICABLE,
            inEffectFrom: getDateFormat(inEffectFrom) || NOT_APPLICABLE,
            inEffectTo: getDateFormat(inEffectTo) || '',
          };
        }),
      );
    }
  }, [modemInfo]);

  const handleRemoveClick = (row) => {
    setModemToRemove(row);
    setShowConfirmDialog(true);
  };

  const handleConfirmRemove = async () => {
    if (!modemToRemove) return;

    if (isResyncEnabled) {
      // Check resync status before removing
      if (machineId) {
        try {
          const resyncResult = await getAssetResyncStatus(machineId).unwrap();
          if (resyncResult?.data?.resync) {
            setResyncWarning(
              'A resync is currently in progress for this asset. Please try again after resync is complete.',
            );
            setShowConfirmDialog(false);
            setModemToRemove(null);
            return;
          }
        } catch {
          // If API fails, proceed
        }
      }

      // Check modem resync status before removing
      if (modemToRemove?.id) {
        try {
          const modemResyncResult = await getModemResyncStatus(modemToRemove.id).unwrap();
          if (modemResyncResult?.data?.resync) {
            setResyncWarning(
              'A resync is currently in progress for this modem. Please try again after resync is complete.',
            );
            setShowConfirmDialog(false);
            setModemToRemove(null);
            return;
          }
        } catch {
          // If API fails, proceed
        }
      }
    }

    try {
      await removeModemFromMachine({
        id: modemToRemove.id,
        rest: {
          modemId: modemToRemove.id,
          updatedBy,
        },
      }).unwrap();
      setSnackbarOpen(true);
      setSnackbarError('');
      setLocalData((prev) => prev.filter((item) => item.id !== modemToRemove.id));
      if (typeof refetchModems === 'function') {
        refetchModems();
      }
      refetachMachine();
      refetchCurrentMachineDetails();
      setShowConfirmDialog(false);
      setModemToRemove(null);
    } catch (error) {
      setSnackbarError(error?.data?.message || error?.message || 'Failed to remove modem. Please try again.');
      setSnackbarOpen(true);
      setShowConfirmDialog(false);
      setModemToRemove(null);
    }
  };

  const handleCancelRemove = () => {
    setShowConfirmDialog(false);
    setModemToRemove(null);
  };

  const handleConfirmDialogClick = (value) => {
    if (value === 'confirm') {
      handleConfirmRemove();
    } else {
      handleCancelRemove();
    }
  };

  const columns = useMemo(
    () => [
      ...COLUMNS.MACHINE_MODEM_DETAILS_TABLE,
      {
        Header: 'REMOVE',
        id: 'remove',
        Cell: ({ row }) => (
          <Button variant="outlined" color="error" size="small" onClick={() => handleRemoveClick(row.original)}>
            REMOVE
          </Button>
        ),
        width: 100,
      },
    ],
    [updatedBy],
  );

  return (
    <>
      {resyncWarning && (
        <Box sx={{ mb: 2 }}>
          <MuiAlert severity="warning" onClose={() => setResyncWarning('')}>
            {resyncWarning}
          </MuiAlert>
        </Box>
      )}
      <ReactTable data={localData} columns={columns} />
      <ConfirmationModal
        isOpen={showConfirmDialog}
        title="Remove Modem"
        msg="Are you sure you want to remove this modem from the machine?"
        isLoading={isRemoving}
        buttons={[
          { text: 'REMOVE', value: 'confirm' },
          { text: 'CANCEL', value: 'cancel' },
        ]}
        onClick={handleConfirmDialogClick}
      />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => {
          setSnackbarOpen(false);
          setSnackbarError('');
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          severity={snackbarError ? 'error' : 'success'}
          onClose={() => {
            setSnackbarOpen(false);
            setSnackbarError('');
          }}
        >
          {snackbarError ? snackbarError : 'Successfully removed'}
        </MuiAlert>
      </Snackbar>
    </>
  );
};

export default ModemDetailView;
